import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EstadoRegistro, FiltrosBitacora, IaService, RegistroIA, TipoRegistro } from 'src/app/services/ia/ia.service';
import {
  BADGE_ESTADO, ETIQUETA_ESTADO, ETIQUETA_TIPO, etiquetaFila, motivoLegible, errorDeIA, fechaCorta, fechaHora, ms, nf, tituloAgente, usd, usdCorto,
} from 'src/app/services/ia/ia-formato';

/**
 * SOLAPA «BITÁCORA» — `console/ia/bitacora`. Qué hizo la IA, una línea por llamada.
 *
 * 🔴 «OMITIDO» ES UN FILTRO DE PRIMERA CLASE, al lado de Exitoso y Error (regla
 * del piloto, `solapa-bitacora.tsx`). La mitad de las preguntas son «¿por qué NO
 * corrió?» — se apagó, se llegó al tope, no había clave — y no «¿por qué falló?».
 *
 * 📌 La fila se abre EN SU LUGAR, debajo, nunca en un modal: el detalle no tiene
 * que tapar la lista contra la que se compara.
 *
 * 📍 Sin columna «Agente»: hoy contesta uno solo, y está en el filtro y en el
 * detalle. Con ella la tabla no cabía y «Costo» —lo que más se mira— quedaba
 * fuera de la vista, detrás del desplazamiento.
 *
 * 📍 Los filtros viajan en la dirección (`?comercio=MT&estado=OMITIDO`): la ficha
 * del comercio enlaza aquí con su código puesto, y el enlace se puede compartir.
 */
@Component({
  selector: 'app-ia-bitacora',
  templateUrl: './ia-bitacora.component.html',
  styleUrls: ['./ia-bitacora.component.css']
})
export class IaBitacoraComponent implements OnInit {

  static readonly POR_PAGINA = 50;

  readonly ESTADOS: (EstadoRegistro | '')[] = ['', 'OK', 'FALLA', 'OMITIDO'];
  readonly TIPOS: TipoRegistro[] = ['caso', 'llamada', 'prueba'];

  estado: EstadoRegistro | '' = '';
  agente = '';
  tipo: TipoRegistro | '' = '';
  comercio = '';
  desde = '';
  hasta = '';

  agentes: string[] = ['asistente-comercio', 'asistente-creditotal'];
  comercios: { codigo: string; nombre: string }[] = [];

  cargando = true;
  trayendoMas = false;
  error: string | null = null;
  filas: RegistroIA[] = [];
  total = 0;
  abierta: string | null = null;

  readonly ETIQUETA_ESTADO = ETIQUETA_ESTADO;
  readonly BADGE_ESTADO = BADGE_ESTADO;
  readonly ETIQUETA_TIPO = ETIQUETA_TIPO;
  readonly etiquetaFila = etiquetaFila;
  readonly motivoLegible = motivoLegible;
  readonly fechaHora = fechaHora;
  readonly fechaCorta = fechaCorta;
  readonly ms = ms;
  readonly nf = nf;
  readonly usd = usd;
  readonly usdCorto = usdCorto;
  readonly tituloAgente = tituloAgente;

  constructor(private _ia: IaService, private ruta: ActivatedRoute, private router: Router) { }

  ngOnInit(): void {
    const q = this.ruta.snapshot.queryParamMap;
    this.comercio = (q.get('comercio') || '').toUpperCase();
    const e = q.get('estado') as EstadoRegistro;
    this.estado = (['OK', 'FALLA', 'OMITIDO'] as string[]).includes(e) ? e : '';
    this.agente = q.get('agente') || '';
    const t = q.get('tipo') as TipoRegistro;
    this.tipo = (this.TIPOS as string[]).includes(t) ? t : '';
    this.desde = q.get('desde') || '';
    this.hasta = q.get('hasta') || '';

    // Las listas de los desplegables: si fallan, quedan las conocidas.
    this._ia.getAgentes()
      .then((a) => { if (Array.isArray(a) && a.length) this.agentes = a.map((x) => x.agente); })
      .catch(() => { /* quedan las conocidas */ });
    this._ia.getComercios()
      .then((c) => { this.comercios = (Array.isArray(c) ? c : []).map((x) => ({ codigo: x.codigo, nombre: x.nombre })); })
      .catch(() => { this.comercios = []; });

    this.traer(0);
  }

  private get filtros(): FiltrosBitacora {
    return {
      ...(this.estado ? { estado: this.estado } : {}),
      ...(this.agente ? { agente: this.agente } : {}),
      ...(this.tipo ? { tipo: this.tipo } : {}),
      ...(this.comercio ? { comercio: this.comercio.trim().toUpperCase() } : {}),
      ...(this.desde ? { desde: this.desde } : {}),
      ...(this.hasta ? { hasta: this.hasta } : {}),
    };
  }

  get hayFiltros(): boolean { return Object.keys(this.filtros).length > 0; }

  /** Cambió un filtro: la dirección lo recuerda y se vuelve a la primera página. */
  aplicar = (): void => {
    this.router.navigate([], {
      relativeTo: this.ruta, replaceUrl: true,
      queryParams: {
        comercio: this.comercio || null, estado: this.estado || null, agente: this.agente || null,
        tipo: this.tipo || null, desde: this.desde || null, hasta: this.hasta || null,
      },
    });
    this.traer(0);
  };

  elegirEstado = (e: EstadoRegistro | ''): void => { this.estado = e; this.aplicar(); };

  limpiar = (): void => {
    this.estado = ''; this.agente = ''; this.tipo = ''; this.comercio = ''; this.desde = ''; this.hasta = '';
    this.aplicar();
  };

  traer = async (salto: number): Promise<void> => {
    if (salto === 0) { this.cargando = true; this.abierta = null; } else { this.trayendoMas = true; }
    this.error = null;
    try {
      const r = await this._ia.getBitacora({ ...this.filtros, limite: IaBitacoraComponent.POR_PAGINA, salto });
      const nuevas = Array.isArray(r?.filas) ? r.filas : [];
      this.filas = salto === 0 ? nuevas : [...this.filas, ...nuevas];
      this.total = Number(r?.total ?? this.filas.length);
    } catch (e: any) {
      if (salto === 0) { this.filas = []; this.total = 0; }
      this.error = errorDeIA(e);
    } finally {
      this.cargando = false;
      this.trayendoMas = false;
    }
  };

  idDe = (r: RegistroIA, i: number): string => r._id || r.traceId || String(i);

  alternar = (r: RegistroIA, i: number): void => {
    const id = this.idDe(r, i);
    this.abierta = this.abierta === id ? null : id;
  };

  comercioEnLista = (codigo: string): boolean => this.comercios.some((c) => c.codigo === codigo);

  nombreComercio = (codigo: string | null | undefined): string => {
    if (!codigo) return '—';
    return this.comercios.find((c) => c.codigo === codigo)?.nombre || codigo;
  };
}
